/**
 * PATH: packages/email-hub/src/feature/components/email-reader/ReaderContent.tsx
 * FILE_ID: EMAILHUB-EMAILREADER-CONTENT-FRAME-001
 * PURPOSE: Assemble typed reader content leaves without owning behavior.
 */
import { InPageFindBar } from '@quotech/ui'
import { isEmailHubFeatureEnabled } from '../../../_internal/flags'
import { ReaderBody } from './ReaderBody'
import { ReaderRail } from './ReaderRail'
import { ReaderSender } from './ReaderSender'
import { ReaderWorkflowSections } from './ReaderWorkflowSections'
import type { EmailReaderController } from './useEmailReaderController'

export function ReaderContent({
  controller,
}: {
  controller: EmailReaderController
}) {
  // Flag-OFF safety (change #269): the legacy sender card stays until the
  // shared whole header owns identity + signals for this surface.
  const wholeHeaderEnabled = isEmailHubFeatureEnabled('wholeReaderHeader')

  /**
   * Scroll ownership. The row below is `overflow-hidden`, so whichever child
   * owns a scrollport owns the reading experience.
   *
   * Wide: body column scrolls, context rail scrolls independently beside it.
   *
   * Narrow: the rail moves INSIDE the body column, after the message. Two
   * reasons it goes inside rather than becoming a second row:
   *   1. `w-80 shrink-0` never yielded, so beside the body it left ~55px of
   *      message on a phone — and the `overflow-hidden` row meant the rail
   *      itself could not be reached either.
   *   2. Stacking it as a sibling row would need the outer row to scroll,
   *      putting a scrollport around the column that already has one. Inside,
   *      there is exactly one scroller at narrow widths, so a drag anywhere
   *      moves the same surface.
   *
   * The body column's class string is pinned by
   * emailBody.overflow.pin.test.ts (BUG-2026-05-24-MOBILE-EMAIL-BODY-OVERFLOW)
   * and is deliberately left untouched.
   */
  const stackContext = controller.isNarrow

  return (
    <div className="flex-1 overflow-hidden flex">
      <div
        ref={controller.scrollRef}
        className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-3 md:p-6"
      >
        {!wholeHeaderEnabled && <ReaderSender controller={controller} />}
        <ReaderWorkflowSections controller={controller} />
        <ReaderBody controller={controller} />
        {stackContext && <ReaderRail controller={controller} stacked />}
      </div>
      <InPageFindBar {...controller.find.bar} />
      {!stackContext && <ReaderRail controller={controller} />}
    </div>
  )
}
